import { useCallback, useEffect, useMemo, useState } from "react";
import { http, downloadUrl, formatApiError } from "../lib/api";
import { safePath } from "../lib/nav";
import { fmtTL, fmtNum, currentMonthRange } from "../lib/format";
import { FileDown, Receipt, History } from "lucide-react";
import { toast } from "../lib/notify";
import { useSearchParams, useNavigate } from "react-router-dom";
import SearchableSelect from "../components/SearchableSelect";
import PeriodPicker from "../components/claims/PeriodPicker";
import MultiSelectFilter from "../components/MultiSelectFilter";
import ClaimHistory from "../components/ClaimHistory";
import CorrectionsBlock from "../components/claims/CorrectionsBlock";
import CorrectionHistoryDialog from "../components/claims/CorrectionHistoryDialog";
import ClaimGroupedTable from "../components/claims/ClaimGroupedTable";
import { SummaryRow } from "../components/claims/SummaryRow";
import { useClaimInvoices } from "../components/claims/useClaimInvoices";

export default function CustomerClaim() {
  const [projects, setProjects] = useState([]);
  const projectOptions = useMemo(() => [...projects].sort((a, b) => (a.name || "").localeCompare(b.name || "", "tr")).map((p) => ({ value: p.id, label: p.name })), [projects]);
  const [regions, setRegions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState(() => {
    const m = currentMonthRange();
    return {
      project_id: searchParams.get("proje") || "",
      region_ids: (searchParams.get("bolge") || "").split(",").filter(Boolean),
      category_id: searchParams.get("ana") || "",
      date_from: searchParams.get("bas") || m.from,
      date_to: searchParams.get("bit") || m.to,
    };
  });
  const regionOptions = useMemo(() => regions.filter((r) => !filters.project_id || r.project_id === filters.project_id).map((r) => ({ value: r.id, label: r.name })), [regions, filters.project_id]);
  const categoryOptions = useMemo(() => categories.filter((c) => c.scope !== "projects" || (c.project_ids || []).includes(filters.project_id)).map((c) => ({ value: c.id, label: c.name })), [categories, filters.project_id]);
  const [data, setData] = useState({ rows: [], summary: { total_quantity: 0, total_customer: 0 } });
  const [creating, setCreating] = useState(false);
  const [claimInvoices, reloadClaimInvoices] = useClaimInvoices(filters.project_id ? { direction: "income", project_id: filters.project_id } : null);
  const [focusInvoiceId, setFocusInvoiceId] = useState(null);   // Hakediş Oluştur sonrası Önceki Hakedişler'de açık gelecek fatura
  const [showCorrHistory, setShowCorrHistory] = useState(false);   // Düzeltme Geçmişi penceresi (bölgenin DÜZELTME FARKLARI şubesi, tüm dönemler)
  const projRegions = useMemo(() => regions.filter((r) => r.project_id === filters.project_id), [regions, filters.project_id]);
  const claimReturnTo = `/musteri-hakedis?proje=${filters.project_id}&bolge=${filters.region_ids.join(",")}&ana=${filters.category_id}&bas=${filters.date_from}&bit=${filters.date_to}`;

  const createInvoice = async () => {
    if (!filters.project_id) return toast.error("Hakediş faturası için önce proje seçin");
    if (!filters.category_id) return toast.error("Hakediş oluşturmak için tek bir Ana Başlık seçin");
    if (data.rows.length === 0) return toast.error("Seçili filtrelerle hakediş kaydı yok");
    if (data.pending_approval) return toast.error(`${data.pending_approval} hizmet kaydı onay bekliyor / reddedilmiş — hakediş oluşturmadan önce onaylar tamamlansın (Hizmet Onayları).`, { duration: 9000 });
    if (!window.confirm(`${fmtTL(data.summary.total_customer)} tutarında gelir faturası (SAS) oluşturulsun mu?`)) return;
    setCreating(true);
    try {
      const { data: inv } = await http.post("/reports/customer-claim/create-invoice", {
        project_id: filters.project_id,
        region_ids: filters.region_ids.length ? filters.region_ids : null,
        category_id: filters.category_id || null,
        date_from: filters.date_from || null,
        date_to: filters.date_to || null,
      });
      toast.success(`${inv.invoice_no} numaralı taslak hakediş faturası oluşturuldu — aşağıda özet açıldı; ± düzeltme satırı ekleyebilirsiniz.`);
      reloadClaimInvoices();
      setFocusInvoiceId(inv.id);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  useEffect(() => {
    Promise.all([
      http.get("/projects").then((r) => setProjects(r.data)),
      http.get("/regions").then((r) => setRegions(r.data)),
      http.get("/categories").then((r) => setCategories(r.data)),
    ]);
  }, []);

  const buildParams = useCallback(() => {
    const p = Object.fromEntries(Object.entries(filters).filter(([k, v]) => k !== "region_ids" && v));
    if (filters.region_ids.length) p.region_ids = filters.region_ids.join(",");
    return p;
  }, [filters]);
  const load = useCallback(() => {
    http.get("/reports/customer-claim", { params: buildParams() }).then((r) => { setData(r.data); if (r.data.pending_approval) toast.warning(`${r.data.pending_approval} onaysız hizmet kaydı hakedişe dahil edilmedi — önce hizmeti onaylayın (Hizmet Onayları).`, { duration: 8000 }); });
  }, [buildParams]);
  useEffect(() => { load(); }, [load]);
  const navigate = useNavigate();
  const returnTo = safePath(searchParams.get("don"));
  useEffect(() => {
    if (!returnTo) return;
    const onKey = (e) => { if (e.key === "Escape" && !e.target.closest("input,textarea,select")) navigate(returnTo); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [returnTo, navigate]);

  const qs = new URLSearchParams(buildParams()).toString();
  const excelHref = downloadUrl(`/reports/customer-claim/export${qs ? "?" + qs : ""}`);

  return (
    <div className="space-y-6" data-testid="page-customer-claim">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Müşteri Hakedişi</h1>
          {returnTo && <div className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-orange-100 text-orange-800 text-[11px] font-semibold" data-testid="claim-return-hint">Bağlantıyla gelindi — Hakedişi Onayla ya da ESC ile aynı filtrelerle geri dönülür</div>}
          <p className="text-sm text-slate-500 mt-1">Taşeron alış fiyatı ve taşeron adı bu raporda gösterilmez.</p>
        </div>
      </div>

      <div className={`rounded-lg border-2 p-4 space-y-2 transition-colors ${filters.project_id ? "border-emerald-300 bg-emerald-50/40" : "border-amber-400 bg-amber-50/60"}`} data-testid="claim-project-step">
        <label className="block text-sm font-bold text-slate-900 uppercase tracking-wide">1. Adım — Proje Seçin</label>
        <div className="[&_input]:py-3 [&_input]:text-base [&_input]:font-semibold">
          <SearchableSelect testid="filter-project" value={filters.project_id} onChange={(v) => setFilters({ ...filters, project_id: v, region_ids: [], category_id: "" })}
            placeholder="Proje adı yazarak arayın…"
            options={projectOptions} />
        </div>
        {!filters.project_id && <div className="text-xs text-amber-800 font-medium" data-testid="claim-project-first-hint">Önce proje seçin — filtreler ve hakediş oluşturma proje seçiminden sonra açılır.</div>}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MultiSelectFilter testid="filter-region" label="Bölge (çoklu seçim)" disabled={!filters.project_id} values={filters.region_ids} onChange={(v) => setFilters({ ...filters, region_ids: v })} emptyText={filters.project_id ? "Bu projede tanımlı bölge yok — tüm lokasyonlar dahil" : "Önce proje seçin"} options={regionOptions} />
          <SelectFilter testid="filter-category" label="Ana Başlık" disabled={!filters.project_id} allOption={false} value={filters.category_id} onChange={(v) => setFilters({ ...filters, category_id: v })} options={categoryOptions} />
          <div className="col-span-2">
            <PeriodPicker from={filters.date_from} to={filters.date_to} disabled={!filters.project_id} onChange={(f, t) => setFilters({ ...filters, date_from: f, date_to: t })} testid="filter-period" />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 flex-wrap border-t border-slate-100 pt-3" data-testid="claim-actions">   {/* işlemler: filtrelerin (bitiş tarihinin) hemen altında */}
          <button onClick={createInvoice} disabled={!filters.project_id || creating} data-testid="btn-create-claim-invoice"
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 disabled:opacity-50 transition-colors">
            <Receipt className="w-4 h-4" /> {creating ? "Oluşturuluyor…" : "Hakediş Oluştur"}
          </button>
          <a href={excelHref} target="_blank" rel="noreferrer" className="excel-btn" data-testid="btn-export-excel">
            <FileDown className="w-4 h-4" /> Excel{"\u2019"}e Aktar
          </a>
          <button type="button" onClick={() => setShowCorrHistory(true)} disabled={projRegions.length === 0} data-testid="btn-corr-history" title={projRegions.length ? "Seçili bölgenin DÜZELTME FARKLARI şubesine dönem dönem girilen tüm ± kayıtlar" : "Önce bölgesi olan bir proje seçin"}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md border border-violet-300 bg-violet-50 text-violet-800 text-sm font-semibold hover:bg-violet-600 hover:text-white hover:border-violet-600 disabled:opacity-50 transition-colors">
            <History className="w-4 h-4" /> Düzeltme Geçmişi
          </button>
        </div>
      </div>
      {showCorrHistory && <CorrectionHistoryDialog regions={projRegions} defaultRegionId={filters.region_ids[0]} onClose={() => setShowCorrHistory(false)} returnTo={claimReturnTo} testid="claim-corr-history" />}

      <ClaimHistory direction="income" projectId={filters.project_id || undefined} returnTo={claimReturnTo} testid="claim-history" focusInvoiceId={focusInvoiceId} />

      {data.services?.length > 0 && (
        <div className="bg-white border-2 border-slate-300 rounded-lg overflow-hidden" data-testid="summary-grand-total">
          <div className="px-5 py-3 border-b border-slate-200 bg-slate-100 flex items-center justify-between flex-wrap gap-2">
            <div className="text-sm font-bold text-slate-900 uppercase tracking-wide">Genel Toplam — Hizmet Özeti</div>
            <div className="text-xs text-slate-500">Dönemdeki tüm hizmet kalemlerinin toplamı; bölge ayrıntısı ve kayıtlar aşağıda.</div>
          </div>
          <table className="w-full tbl">
            <thead className="bg-white">
              <tr>
                {["Hizmet", "Miktar", "Birim", "Birim Fiyat", "Toplam"].map((h, i) => (
                  <th key={h} className={`px-4 py-2 text-xs uppercase tracking-wider text-slate-500 font-semibold ${i === 1 || i >= 3 ? "text-right" : i === 2 ? "text-center" : "text-left"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.services.map((s, i) => <SummaryRow key={`${s.service_id || s.service_name}-${i}`} s={s} priceKey="customer_unit_price" totalKey="customer_total" testid={s.is_correction ? "customer-summary-corrections-row" : undefined} />)}
            </tbody>
            <tfoot className="bg-slate-100 border-t-2 border-slate-300">
              <tr>
                <td colSpan={4} className="px-4 py-3 text-xs font-bold text-slate-900 uppercase tracking-wider">Toplam</td>
                <td className="numeric-cell px-4 py-3 text-sm font-extrabold text-slate-900" data-testid="customer-summary-total">{fmtTL(data.summary.total_customer)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <ClaimGroupedTable rows={data.rows} corrections={data.corrections} summaryTotal={data.summary.total_customer} invoices={claimInvoices} returnTo={claimReturnTo} testid="customer-claim-groups" />

      <CorrectionsBlock corrections={data.corrections} totalKey="customer_total" testid="customer-claim-corrections" />

      {((data.regions && data.regions.length > 0) || data.atm_penalties?.total > 0) && (
        <div className="space-y-5" data-testid="customer-service-summary">
          {data.regions?.length > 0 && <div>
            <div className="text-sm font-semibold text-slate-900">Hizmet Özeti — Bölge Bazında</div>
            <div className="text-xs text-slate-500">Her bölge için hizmet kalemi özeti; genel toplam yukarıda, Önceki Hakedişler'in altında.</div>
          </div>}

          {(data.regions || []).map((rg) => (
            <div key={rg.region_name} className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid={`summary-region-${rg.region_name}`}>
              <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
                <div className="text-sm font-bold text-slate-900 uppercase tracking-wide">{rg.region_name}</div>
                <div className="text-xs font-mono font-semibold text-slate-700">Bölge Toplamı: {fmtTL(rg.total)}</div>
              </div>
              <table className="w-full tbl">
                <thead className="bg-white">
                  <tr>
                    {["Hizmet", "Miktar", "Birim", "Birim Fiyat", "Toplam"].map((h, i) => (
                      <th key={h} className={`px-4 py-2 text-xs uppercase tracking-wider text-slate-500 font-semibold ${i === 1 || i >= 3 ? "text-right" : i === 2 ? "text-center" : "text-left"}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rg.services.map((s, i) => <SummaryRow key={`${s.service_id || s.service_name}-${i}`} s={s} priceKey="customer_unit_price" totalKey="customer_total" />)}
                </tbody>
              </table>
            </div>
          ))}

          {data.atm_penalties && data.atm_penalties.total > 0 && (
            <div className="bg-white border-2 border-red-300 rounded-lg overflow-hidden" data-testid="customer-claim-atm-penalties">
              <div className="px-5 py-3 border-b border-red-200 bg-red-50 flex items-center justify-between flex-wrap gap-2">
                <div><div className="text-sm font-bold text-slate-900 uppercase tracking-wide">ATM Ceza Kesintileri (WILCO'ya kesilen)</div><div className="text-xs text-slate-500">Projenin ATM'lerine dönem aylarında girilen WILCO cezaları — hakediş toplamından düşüldü.</div></div>
                <div className="text-sm font-extrabold text-red-700" data-testid="customer-claim-atm-penalty-total">− {fmtTL(data.atm_penalties.total)}</div>
              </div>
              <table className="w-full tbl">
                <thead className="bg-white"><tr>{["Ay", "ATM No", "ATM Adı", "Açıklama", "Giren", "Ceza"].map((h, i) => <th key={h} className={`px-4 py-2 text-xs uppercase tracking-wider text-slate-500 font-semibold ${i === 5 ? "text-right" : "text-left"}`}>{h}</th>)}</tr></thead>
                <tbody>
                  {data.atm_penalties.rows.map((p) => (
                    <tr key={p.id} className="border-t border-slate-100" data-testid={`customer-claim-atm-penalty-row-${p.id}`}>
                      <td className="px-4 py-2 text-xs">{p.month}</td>
                      <td className="px-4 py-2 text-xs font-mono font-bold">{p.branch_code}</td>
                      <td className="px-4 py-2 text-xs font-semibold">{p.location_name}</td>
                      <td className="px-4 py-2 text-xs">{p.note || "—"}</td>
                      <td className="px-4 py-2 text-xs text-slate-500">{p.by}</td>
                      <td className="numeric-cell px-4 py-2 text-xs font-semibold text-red-700">− {fmtTL(p.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SelectFilter({ label, value, onChange, options, testid, disabled = false, allOption = true }) {
  return (
    <div className="space-y-1">
      <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</label>
      <select data-testid={testid} disabled={disabled} value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-2.5 py-1.5 text-xs border border-slate-300 rounded-md disabled:bg-slate-50 disabled:text-slate-400">
        <option value="">{allOption ? "Tümü" : "Seçiniz…"}</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
